/**
 * Open Presentation Feature
 *
 * A self-contained module for opening and loading presentation JSON files.
 * This is a new implementation that does not depend on any legacy open/load/import code.
 */

import { JSON_ACCEPT, pickFile } from '../../utils/filePicker.js';

/**
 * Valid BOPPPS section IDs used in presentations
 */
const BOPPPS_SECTION_IDS = [
  'bridge-in',
  'objectives',
  'pre-assessment',
  'participatory-learning',
  'post-assessment',
  'summary'
];

/**
 * Stable format section IDs (use underscores instead of hyphens)
 */
const STABLE_SECTION_IDS = [
  'bridge_in',
  'objectives',
  'pre_assessment',
  'participatory_learning',
  'post_assessment',
  'summary'
];

/**
 * Error types for presentation loading
 */
export const OpenPresentationErrorType = {
  CANCELLED: 'CANCELLED',
  INVALID_JSON: 'INVALID_JSON',
  INVALID_FORMAT: 'INVALID_FORMAT',
  FILE_READ_ERROR: 'FILE_READ_ERROR'
};

/**
 * Custom error class for presentation loading errors.
 *
 * `details` carries whatever the failure screen can use to tell someone what
 * is actually wrong with their file — the line a syntax error sits on, the
 * full list of validation complaints, the keys the file did contain. Callers
 * that only want a sentence can keep ignoring it.
 */
export class OpenPresentationError extends Error {
  constructor(type, message, cause = null, details = {}) {
    super(message);
    this.name = 'OpenPresentationError';
    this.type = type;
    this.cause = cause;
    this.details = details;
  }
}

/**
 * Point at the place in the source text where JSON.parse gave up.
 *
 * Engines say this three different ways and only one of them is precise:
 *
 *   - "…at position 412 (line 4 column 1)" — exact, so it is used as given;
 *   - `Unexpected token ',', ..."k": ,}" is not valid JSON` — V8's newer
 *     wording, which quotes a window of the source instead of an offset. The
 *     window can be located in the text, but the offset of the bad character
 *     inside it is not consistent, so this reports the neighbourhood and marks
 *     itself `approximate` rather than inventing a column;
 *   - "Unexpected end of JSON input" — the file simply stops, so the end of it
 *     is the interesting place.
 *
 * Anything else returns null; a wrong line number is worse than none. The
 * position is resolved against the text here rather than stored for later,
 * because decks run to megabytes and an error object should not keep one alive.
 *
 * @param {string} text - The text that failed to parse
 * @param {Error} error - The SyntaxError from JSON.parse
 * @returns {{ line: number, column: number|null, excerpt: string, approximate?: boolean } | null}
 */
export function locateJsonSyntaxError(text, error) {
  const message = String(error?.message || '');
  if (typeof text !== 'string') return null;

  const explicit = /line (\d+) column (\d+)/i.exec(message);
  if (explicit) {
    const line = Number(explicit[1]);
    return { line, column: Number(explicit[2]), excerpt: excerptLine(text, line) };
  }

  const positional = /position (\d+)/i.exec(message);
  if (positional) {
    const position = Math.min(Number(positional[1]), text.length);
    const before = text.slice(0, position);
    const line = before.split('\n').length;
    return { line, column: position - before.lastIndexOf('\n'), excerpt: excerptLine(text, line) };
  }

  const quoted = /(\.\.\.)?"([\s\S]*?)"(\.\.\.)? is not valid JSON/.exec(message);
  if (quoted && quoted[2]) {
    const snippet = quoted[2];
    const index = text.indexOf(snippet);
    if (index >= 0) {
      return {
        line: text.slice(0, index).split('\n').length,
        column: null,
        excerpt: clamp(snippet.split('\n').map((part) => part.trim()).filter(Boolean).join(' ⏎ ')),
        approximate: true
      };
    }
  }

  if (/unexpected end of (json )?input/i.test(message)) {
    const lines = text.split('\n');
    const lastFilled = findLastIndex(lines, (line) => line.trim() !== '');
    return {
      line: lastFilled >= 0 ? lastFilled + 1 : lines.length,
      column: null,
      excerpt: lastFilled >= 0 ? clamp(lines[lastFilled].trim()) : '',
      approximate: true
    };
  }

  return null;
}

function findLastIndex(items, predicate) {
  for (let index = items.length - 1; index >= 0; index -= 1) {
    if (predicate(items[index])) return index;
  }
  return -1;
}

function clamp(value, limit = 160) {
  if (typeof value !== 'string') return '';
  return value.length > limit ? `${value.slice(0, limit - 3)}…` : value;
}

/** The offending line, trimmed to something that fits on a screen. */
function excerptLine(text, line) {
  if (typeof text !== 'string' || !Number.isFinite(line) || line < 1) return '';
  const raw = text.split('\n')[line - 1];
  if (typeof raw !== 'string') return '';
  return clamp(raw.trim());
}

/** The top-level keys a file did have, which is usually the giveaway. */
function topLevelKeys(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return [];
  return Object.keys(data).slice(0, 12);
}

/**
 * Validates that the parsed data looks like a valid presentation.
 * Supports both internal format (v4.0.0) and stable format (schema_version 1.0.0).
 *
 * IMPORTANT: Check order matters!
 * - Internal format has: version: '4.0.0', format: 'boppps_lesson'
 * - Stable format has: schema_version: '1.0.0', format: 'boppps_lesson', blocks: [...]
 * Both share format: 'boppps_lesson', so we must check for 'version' FIRST.
 *
 * @param {unknown} data - The parsed JSON data to validate
 * @returns {{ valid: boolean, format: 'internal' | 'stable' | null, errors: string[] }}
 */
export function validatePresentationData(data) {
  const errors = [];

  // Must be an object
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return {
      valid: false,
      format: null,
      errors: ['Data must be a JSON object']
    };
  }

  // CHECK INTERNAL FORMAT FIRST (v4.0.0, full_deck, slim_deck)
  // These have 'version' field or 'schema_type' field, and may also have format: 'boppps_lesson'
  if (data.version || data.schema_type === 'full_deck' || data.schema_type === 'slim_deck') {
    // Validate internal format minimum requirements
    if (!data.sections || typeof data.sections !== 'object') {
      errors.push('Missing or invalid "sections" object');
    } else if (!Array.isArray(data.sections)) {
      // v4.0.0 format has sections as an object
      const sectionKeys = Object.keys(data.sections);
      const hasInternalSections = sectionKeys.some(key => {
        const section = data.sections[key];
        // Section value can be array (slim deck) or object with blocks (full deck)
        return section && (Array.isArray(section) || typeof section === 'object');
      });
      if (!hasInternalSections && sectionKeys.length === 0) {
        errors.push('No valid BOPPPS sections found in sections object');
      }
    } else {
      // Legacy array format
      const hasArraySections = data.sections.some(
        section => section && typeof section === 'object' &&
                   (section.id && BOPPPS_SECTION_IDS.includes(section.id))
      );
      if (!hasArraySections && data.sections.length === 0) {
        errors.push('No valid BOPPPS sections found in sections array');
      }
    }

    return {
      valid: errors.length === 0,
      format: 'internal',
      errors
    };
  }

  // CHECK STABLE FORMAT (schema_version 1.0.0 - LLM template format)
  // Only treat as stable if schema_version is explicitly '1.0.0'
  if (data.schema_version === '1.0.0') {
    // Validate stable format minimum requirements
    if (!data.sections || typeof data.sections !== 'object') {
      errors.push('Missing or invalid "sections" object');
    } else {
      // Check for at least some BOPPPS sections (underscore format)
      const hasStableSections = STABLE_SECTION_IDS.some(
        id => data.sections[id] && typeof data.sections[id] === 'object'
      );
      if (!hasStableSections) {
        errors.push('No valid BOPPPS sections found in stable format');
      }
    }

    if (!data.blocks || !Array.isArray(data.blocks)) {
      errors.push('Missing or invalid "blocks" array');
    }

    return {
      valid: errors.length === 0,
      format: 'stable',
      errors
    };
  }

  // CHECK LEGACY FORMAT (just has sections, no version markers)
  if (data.sections) {
    if (typeof data.sections !== 'object') {
      errors.push('Invalid "sections" field - must be object or array');
    }
    return {
      valid: errors.length === 0,
      format: 'legacy',
      errors
    };
  }

  // Unknown format
  errors.push('Unrecognized presentation format. File must contain "sections" or valid format markers.');
  return {
    valid: false,
    format: null,
    errors
  };
}

/**
 * Reads a file as text
 *
 * @param {File} file - The file to read
 * @returns {Promise<string>} The file contents as a string
 */
function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => resolve(event.target.result);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
}

/**
 * Opens a file picker dialog for JSON files.
 *
 * Delegates to the shared picker, which keeps the input in the document while
 * the dialog is open — iOS Safari ignores a click on a detached input, so this
 * button did nothing at all on a phone.
 *
 * @returns {Promise<File | null>} The selected file, or null if cancelled
 */
function openFilePicker() {
  return pickFile({ accept: JSON_ACCEPT });
}

/**
 * Parses and validates presentation JSON held as text.
 *
 * This is the half of `openPresentationFile` that has nothing to do with
 * files, so that pasted JSON — an assistant's reply, straight out of the chat
 * window — goes through exactly the same parse, the same validation and the
 * same typed errors as a file does. A paste that opens but a file that does
 * not (or the reverse) is the kind of difference nobody can explain later.
 *
 * A fenced ```json block is unwrapped first: assistants add the fence however
 * firmly they are told not to, and refusing the paste over three backticks
 * helps nobody.
 *
 * @param {string} text - The JSON to load
 * @returns {{ data: object, format: string }}
 * @throws {OpenPresentationError} If the text is empty, unparseable or not a deck
 */
export function parsePresentationText(text) {
  const source = typeof text === 'string' ? text.trim() : '';
  if (!source) {
    throw new OpenPresentationError(
      OpenPresentationErrorType.CANCELLED,
      'Nothing was pasted'
    );
  }

  const fenced = /^```(?:json)?\s*([\s\S]*?)\s*```$/i.exec(source);
  const body = fenced ? fenced[1].trim() : source;

  let data;
  try {
    data = JSON.parse(body);
  } catch (error) {
    throw new OpenPresentationError(
      OpenPresentationErrorType.INVALID_JSON,
      'That is not valid JSON',
      error,
      {
        reason: error?.message || '',
        location: locateJsonSyntaxError(body, error)
      }
    );
  }

  const validation = validatePresentationData(data);
  if (!validation.valid) {
    throw new OpenPresentationError(
      OpenPresentationErrorType.INVALID_FORMAT,
      validation.errors.length > 0
        ? `Not a valid presentation: ${validation.errors[0]}`
        : 'That is not a recognized presentation format',
      null,
      {
        issues: validation.errors,
        detectedFormat: validation.format,
        keys: topLevelKeys(data)
      }
    );
  }

  return { data, format: validation.format };
}

/**
 * Opens a presentation JSON file.
 *
 * This function:
 * 1. Opens a file picker limited to .json files
 * 2. Reads the selected file
 * 3. Parses and validates it with `parsePresentationText`
 * 4. Returns the parsed data
 *
 * @returns {Promise<{ data: object, fileName: string }>} The parsed presentation data and filename
 * @throws {OpenPresentationError} If loading fails or is cancelled
 */
export async function openPresentationFile() {
  // Step 1: Open file picker
  const file = await openFilePicker();

  if (!file) {
    throw new OpenPresentationError(
      OpenPresentationErrorType.CANCELLED,
      'File selection was cancelled'
    );
  }

  // Step 2: Read file contents
  let contents;
  try {
    contents = await readFileAsText(file);
  } catch (error) {
    throw new OpenPresentationError(
      OpenPresentationErrorType.FILE_READ_ERROR,
      'Failed to read the selected file',
      error,
      { fileName: file.name }
    );
  }

  // Step 3: Parse and validate, which is shared with the paste route. An empty
  // file is a bad file rather than a cancellation, so it is restated here.
  let parsed;
  try {
    parsed = parsePresentationText(contents);
  } catch (error) {
    if (error.type === OpenPresentationErrorType.CANCELLED) {
      throw new OpenPresentationError(
        OpenPresentationErrorType.INVALID_JSON,
        'The file is empty',
        null,
        { fileName: file.name, empty: true }
      );
    }
    // The file name is known here and nowhere below it, so the failure screen
    // gets told which file it is talking about.
    error.details = { ...(error.details || {}), fileName: file.name };
    throw error;
  }

  // Step 4: Return the data
  return {
    data: parsed.data,
    fileName: file.name,
    format: parsed.format
  };
}

export default openPresentationFile;
